/**
 * The built-in table (defs/table.ts) as a data spec.
 *
 * How it works:
 *  - `shape` (rectangular / round) switches between two sets of groups with
 *    `visibleWhen`. In formulas a choice reads as its option index, so
 *    `shape == 1` means round.
 *  - Rectangular top: the footprint rectangle (stretched) plus an inner
 *    inset line at 6 % of the smaller side (formula corners, `fixed` group).
 *  - Round top: a 24-sided ellipse drawn at the default size and stretched
 *    with the footprint, so it always touches the four edges.
 *  - Chairs: every chair is the chair symbol (assets/chair.svg), 0.45 ×
 *    0.45 m, authored CENTRED ON THE ORIGIN with its backrest at −n, in a
 *    `fixed` group with an `index` repeat: the group `offset` places copy
 *    `i` (0 … count−1) and turns it to face the table. A chair facing the
 *    direction at angle φ is turned by φ − π/2 (the symbol faces +n).
 *     · Long sides: `sideSeats` = one seat per 0.62 m of width on each side,
 *       spread evenly, the front row turned π (facing −n), the back row not
 *       turned (facing +n).
 *     · Head chairs (opt-in): one at each end, only once the table is at
 *       least 0.6 m deep.
 *     · Round table: `roundSeats` = one seat per 0.62 m of the ellipse's
 *       perimeter (Ramanujan's approximation), spread at equal angles
 *       t = 2π·i / count. Each chair sits `chairOffset` out along the
 *       ellipse's outward normal (cos t / a, sin t / b) and faces back
 *       along it.
 */

import { OBJECT_DEFINITION_SCHEMA_VERSION, type ObjectDefinitionSpec, type ObjectPolylineShape, type Vec2 } from 'shared-types';
import chairSvg from '../../assets/chair.svg?raw';
import { svgSymbolShapes } from './svgSymbol';

const W = 2.0;
const D = 0.9;
const HW = W / 2;
const HD = D / 2;
const INSET = Math.min(W, D) * 0.06;
/** Sides of the round top's polygon. */
const ROUND_SEGMENTS = 24;
/** Footprint of the chair symbol (the Chair object's default size). */
const CHAIR_SIZE = 0.45;

/** Round top at the default size (the group stretches it to the live footprint). */
const roundTop: ObjectPolylineShape = {
  id: 'roundTop',
  name: 'Round top',
  kind: 'polyline',
  points: Array.from({ length: ROUND_SEGMENTS }, (_, i): Vec2 => {
    const t = (2 * Math.PI * i) / ROUND_SEGMENTS;
    return [Math.round(HW * Math.cos(t) * 1e6) / 1e6, Math.round(HD * Math.sin(t) * 1e6) / 1e6];
  }),
  closed: true,
};

/** One chair symbol per chair group (each group places its own copies). */
const chairSymbol = (prefix: string, name: string): ObjectPolylineShape[] => svgSymbolShapes('chair', chairSvg, CHAIR_SIZE, CHAIR_SIZE, prefix, name);
const frontChairs = chairSymbol('frontChair', 'Front chair');
const backChairs = chairSymbol('backChair', 'Back chair');
const rightHeadChair = chairSymbol('rightHeadChair', 'Right head chair');
const leftHeadChair = chairSymbol('leftHeadChair', 'Left head chair');
const roundChairs = chairSymbol('roundChair', 'Round table chair');
const ids = (shapes: readonly ObjectPolylineShape[]): string[] => shapes.map((s) => s.id);

/** Angle of seat i around the round table. */
const T = '(2 * pi * i / count)';
/** Outward ellipse normal at T (not normalized). */
const NU = `cos${T} / halfWidth`;
const NN = `sin${T} / halfDepth`;

export const tableSpec: ObjectDefinitionSpec = {
  schemaVersion: OBJECT_DEFINITION_SCHEMA_VERSION,
  anchor: 'POINT',
  constraint: { kind: 'FREE' },
  defaultSize: { width: W, depth: D, height: 0.75 },
  params: [
    {
      key: 'shape',
      kind: 'select',
      label: 'shape',
      default: 'rectangular',
      options: [
        { value: 'rectangular', label: 'rectangular' },
        { value: 'round', label: 'round' },
      ],
    },
    { key: 'chairs', kind: 'boolean', label: 'chairs', default: true },
    { key: 'headChairs', kind: 'boolean', label: 'head chairs', default: false },
  ],
  formulas: [
    { key: 'halfWidth', label: 'Half width', expr: 'max(width / 2, 0.000001)', note: 'Half the width (never 0, it is divided by)' },
    { key: 'halfDepth', label: 'Half depth', expr: 'max(depth / 2, 0.000001)', note: 'Half the depth (never 0, it is divided by)' },
    { key: 'inset', label: 'Inner line inset', expr: 'min(width, depth) * 0.06', note: 'Inner line of the rectangular top: 6 % of the smaller side in from the edge' },
    { key: 'seatPitch', label: 'Space per seat', expr: '0.62', note: 'Room per seat along the edge (chair + elbow room): drives how many chairs fit' },
    { key: 'chairOffset', label: 'Chair distance from the edge', expr: '0.04 + 0.45 / 2', note: 'From the table edge to a chair center: a 4 cm gap plus half the 0.45 m chair' },
    { key: 'sideSeats', label: 'Chairs per long side', expr: 'floor(width / seatPitch)', note: 'Chairs along each long side of a rectangular table' },
    { key: 'headSeats', label: 'Head chairs shown', expr: 'headChairs * (depth >= 0.6)', note: '1 when head chairs are on and the table is at least 0.6 m deep' },
    {
      key: 'perimeter',
      label: 'Round top perimeter',
      expr: 'pi * (3 * (halfWidth + halfDepth) - sqrt((3 * halfWidth + halfDepth) * (halfWidth + 3 * halfDepth)))',
      note: "Perimeter of the round top (Ramanujan's ellipse approximation)",
    },
    { key: 'roundSeats', label: 'Chairs around the round top', expr: 'floor(perimeter / seatPitch)', note: 'Chairs around a round table' },
  ],
  shapes: [
    { id: 'rectTop', name: 'Top', kind: 'rect', min: [-HW, -HD], max: [HW, HD] },
    {
      id: 'insetLine',
      name: 'Inner line',
      kind: 'rect',
      min: [-HW + INSET, -HD + INSET],
      max: [HW - INSET, HD - INSET],
      expr: { 'min.u': '-width / 2 + inset', 'min.n': '-depth / 2 + inset', 'max.u': 'width / 2 - inset', 'max.n': 'depth / 2 - inset' },
    },
    roundTop,
    ...frontChairs,
    ...backChairs,
    ...rightHeadChair,
    ...leftHeadChair,
    ...roundChairs,
  ],
  groups: [
    { id: 'rectTop', name: 'Rectangular top', shapeIds: ['rectTop'], resize: { u: 'stretch', n: 'stretch' }, visibleWhen: { param: 'shape', equals: 'rectangular' } },
    { id: 'insetLine', name: 'Inner line', shapeIds: ['insetLine'], resize: { u: 'fixed', n: 'fixed' }, visibleWhen: { param: 'shape', equals: 'rectangular' } },
    { id: 'roundTop', name: 'Round top', shapeIds: ['roundTop'], resize: { u: 'stretch', n: 'stretch' }, visibleWhen: { param: 'shape', equals: 'round' } },
    {
      id: 'frontChairs',
      name: 'Front chairs',
      shapeIds: ids(frontChairs),
      resize: { u: 'fixed', n: 'fixed' },
      repeat: { kind: 'index', count: { expr: 'sideSeats' } },
      visibleWhen: { param: 'shape', equals: 'rectangular' },
      visibleIf: 'chairs',
      // Seat i centered in its 1/count share of the width, past the +n edge, facing −n.
      offset: { u: '-width / 2 + (i + 0.5) * width / count', n: 'depth / 2 + chairOffset', rotate: 'pi' },
    },
    {
      id: 'backChairs',
      name: 'Back chairs',
      shapeIds: ids(backChairs),
      resize: { u: 'fixed', n: 'fixed' },
      repeat: { kind: 'index', count: { expr: 'sideSeats' } },
      visibleWhen: { param: 'shape', equals: 'rectangular' },
      visibleIf: 'chairs',
      // Same seats past the −n edge, facing +n (the symbol's own direction).
      offset: { u: '-width / 2 + (i + 0.5) * width / count', n: '-(depth / 2 + chairOffset)' },
    },
    {
      id: 'rightHeadChair',
      name: 'Right head chair',
      shapeIds: ids(rightHeadChair),
      resize: { u: 'fixed', n: 'fixed' },
      visibleWhen: { param: 'shape', equals: 'rectangular' },
      visibleIf: 'chairs * headSeats',
      // Past the +u end, facing −u.
      offset: { u: 'width / 2 + chairOffset', rotate: 'pi / 2' },
    },
    {
      id: 'leftHeadChair',
      name: 'Left head chair',
      shapeIds: ids(leftHeadChair),
      resize: { u: 'fixed', n: 'fixed' },
      visibleWhen: { param: 'shape', equals: 'rectangular' },
      visibleIf: 'chairs * headSeats',
      // Past the −u end, facing +u.
      offset: { u: '-(width / 2 + chairOffset)', rotate: '-pi / 2' },
    },
    {
      id: 'roundChairs',
      name: 'Chairs around the round top',
      shapeIds: ids(roundChairs),
      resize: { u: 'fixed', n: 'fixed' },
      repeat: { kind: 'index', count: { expr: 'roundSeats' } },
      visibleWhen: { param: 'shape', equals: 'round' },
      visibleIf: 'chairs',
      // On the ellipse at angle t, pushed chairOffset out along the unit outward normal, facing back along it.
      offset: {
        u: `halfWidth * cos${T} + chairOffset * (${NU}) / hypot(${NU}, ${NN})`,
        n: `halfDepth * sin${T} + chairOffset * (${NN}) / hypot(${NU}, ${NN})`,
        rotate: `atan2(-(${NN}), -(${NU})) - pi / 2`,
      },
    },
  ],
  model3d: null,
};
